import { useState, useCallback } from "react";

import Alert from "ui-components/Alert";
import Modal from "ui-components/Modal";
import Button from "ui-components/Button";
import InputRange from "ui-components/Input-Range";

import { useShareEntity } from "api";
import Divider from "ui-components/Divider";

interface Props {
  entityName: string;
  baseUrl: string;
  entityId: string;
  size?: "small" | "medium" | "large";
  viewHelperText?: React.ReactNode;
  useHelperText?: React.ReactNode;
}

const ShareEntity = ({
  entityName,
  baseUrl,
  entityId,
  size,
  viewHelperText,
  useHelperText,
}: Props) => {
  const [submitStatus, setSubmitStatus] = useState<
    "not-started" | "in-progress" | "completed"
  >("not-started");
  const [error, setError] = useState<string>();
  const [showModal, setShowModal] = useState(false);
  const [canView, setCanView] = useState<string[]>([]);
  const [canUse, setCanUse] = useState<string[]>([]);
  const shareEntity = useShareEntity();

  const reset = useCallback(() => {
    setShowModal(false);
    setCanView([]);
    setCanUse([]);
    setError(undefined);
    setSubmitStatus("not-started");
  }, [setShowModal, setCanUse, setCanView]);

  const onShare = useCallback(async () => {
    const permissions = { can_view: canView, can_use: canUse };
    if (canView.length === 0 && canUse.length === 0) {
      setError("At least ONE user must be set!!!");
      return;
    }
    setSubmitStatus("in-progress");
    try {
      shareEntity({ baseUrl, entityId, permissions });
      setError(undefined);
      setTimeout(reset, 3000);
    } catch (e) {
      setError("Request Failed: Check your inputs!");
    }
    setSubmitStatus("completed");
  }, [canView, canUse, setSubmitStatus, shareEntity, setError]);

  return (
    <div>
      <Button onClick={() => setShowModal(true)} color="gray" size={size}>
        SHARE
      </Button>
      <Modal
        id={`share-${entityName}`}
        ariaLabel={`share-${entityName}`}
        show={showModal}
        handleClose={() => setShowModal(false)}
        title={
          <span>
            Share {entityName} <span className="italic">{entityId}</span>
          </span>
        }
      >
        <>
          <div>
            <InputRange<string>
              label="Users for VIEW access"
              help={viewHelperText}
              type="text"
              range={canView}
              onValueChange={(users) => setCanView(users || [])}
            />
          </div>
          <div className="mt-4">
            <InputRange<string>
              label="Users for USE access"
              help={useHelperText}
              type="text"
              range={canUse}
              onValueChange={(users) => setCanUse(users || [])}
            />
          </div>
          {error && (
            <div className="mt-4">
              <Alert>{error}</Alert>
            </div>
          )}
          {submitStatus === "completed" && !error && (
            <div className="mt-4">
              <Alert variant="success">Successfully Shared!!!</Alert>
            </div>
          )}

          <Divider />
          <p className="text-right">
            <Button onClick={() => setShowModal(false)} color="gray">
              Exit
            </Button>
            <Button onClick={onShare} loading={submitStatus === "in-progress"}>
              SHARE
            </Button>
          </p>
        </>
      </Modal>
    </div>
  );
};

export default ShareEntity;
